import { useState, useRef, useCallback, useEffect } from "react";

/**
 * Swipe-down-to-dismiss hook for mobile bottom-sheet modals.
 *
 * Uses native DOM event listeners (not React synthetic events) so that
 * e.preventDefault() can be called on touchmove — this blocks the browser's
 * native pull-to-refresh gesture which would otherwise conflict.
 *
 * Usage:
 *   const { dragHandleRef, sheetStyle } = useSwipeDown({ onClose: () => setOpen(false) });
 *
 *   <div ref={dragHandleRef} className="sm:hidden ...">  ← drag handle element
 *   <div style={sheetStyle}>                              ← modal sheet container
 */
export function useSwipeDown({ onClose, threshold = 120 }) {
  const [dragY, setDragY] = useState(0);
  const [handleEl, setHandleEl] = useState(null);
  const startY = useRef(null);
  const dragYRef = useRef(0);
  // Keep a stable ref to onClose so the effect doesn't re-run when it changes
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  // Callback ref fires when the drag handle mounts or unmounts
  const dragHandleRef = useCallback((el) => {
    setHandleEl(el);
  }, []);

  useEffect(() => {
    if (!handleEl) return;

    const onTouchStart = (e) => {
      startY.current = e.touches[0].clientY;
    };

    const onTouchMove = (e) => {
      if (startY.current === null) return;
      const delta = e.touches[0].clientY - startY.current;
      if (delta > 0) {
        // Block browser pull-to-refresh — requires passive: false
        e.preventDefault();
        dragYRef.current = delta;
        setDragY(delta);
      }
    };

    const onTouchEnd = () => {
      if (dragYRef.current > threshold) {
        onCloseRef.current();
      }
      dragYRef.current = 0;
      setDragY(0);
      startY.current = null;
    };

    handleEl.addEventListener("touchstart", onTouchStart, { passive: true });
    handleEl.addEventListener("touchmove", onTouchMove, { passive: false });
    handleEl.addEventListener("touchend", onTouchEnd, { passive: true });

    return () => {
      handleEl.removeEventListener("touchstart", onTouchStart);
      handleEl.removeEventListener("touchmove", onTouchMove);
      handleEl.removeEventListener("touchend", onTouchEnd);
    };
  }, [handleEl, threshold]);

  const sheetStyle = {
    transform: `translateY(${dragY}px)`,
    transition:
      dragY === 0 ? "transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)" : "none",
    willChange: "transform",
  };

  return { dragHandleRef, sheetStyle, dragY };
}
